import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stack, StackProps, Typography, Grid, FormControlLabel, Switch, Alert } from "@mui/material";
import FormSection from "../../../Forms/FormSection/form-section";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import TokenIcon from "@mui/icons-material/Token";
import ConfigFormIcon from "../../ConfigFormIcon";
import CellTowerIcon from "@mui/icons-material/CellTower";
import FormAutocomplete from "../../../Forms/FormAutocomplete/form-autocomplete";
import { useAppSelector } from "../../../../Redux/redux-store";
import { GetSectionIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { DbOrderBasketItemInfos } from "../../../../Core/Api/Types/db-order-basket-item-infos";
import { TranskodeGenericOptionsProfile } from "../../../../Core/Api/Types/transkode-generic-options-profile";
import {
  BasketShowOnlyAvailable,
  GetBasketProfiles,
  ProductField,
  SettingRootField,
  TgopField,
  UpdateItemProductRootOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemSettingsTgopOnBasket,
} from "../../../../Redux/Reducers/redux-slice-basket";
import {
  GetSubtitleLabel,
  MakeAudioValues,
  MakeSubtitleBurnedValues,
  MakeSubtitleSdhSidecarValues,
  MakeSubtitleSidecarValues,
} from "../../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import { useDispatch } from "react-redux";
import FormInputText from "../../../Forms/FormInputText/form-input-text";
import { Distinct, True, UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { assetService } from "../../../../Core/Api/services";
import { XmlDeliveryIdentityProfile } from "../../../../Core/Api/Types/xml-delivery-identity-profile";
import { TranskodeVideoProfile } from "../../../../Core/Api/Types/transkode-video-profile";
import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import { PictureOptionCouple } from "../../../../Core/Api/Types/picture-option-couple";
import { GetMetadataByTagName } from "../../../../Core/Utils/asset-object-utils";
import { XmlTechAudioMapping } from "../../../../Core/Api/Types/xml-tech-audio-mapping";
import { SubtitleOption } from "../../../../Core/Api/Types/subtitle-option";
import ConfigCommonSectionFormClips from "../../ConfigCommon/config-common-section-form-clips";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import ConfigCommonSectionFormStitching from "../../ConfigCommon/config-common-section-form-stitching";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import useDidMountEffect from "../../../../Utils/Hooks/useComponentDidMountEffect";
import { AudioMixLanguageCouple } from "../../../../Core/Api/Types/audio-mix-language-couple";
import { BasketItemStatus } from "../../../../Core/Api/Types/basket-item-status";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { TranskodeTimelineSegmentType } from "../../../../Core/Api/Types/transkode-timeline-segment-type";
import { PictureFormat } from "../../../../Core/Api/Types/picture-format";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import ConfigCommonSectionFormAttachments from "../../ConfigCommon/config-common-section-form-attachments";
import { XmlDeliveryIdentityProfilesInfo } from "../../../../Core/Api/Types/xml-delivery-identity-profiles-info";
import ConfigCommonSectionFormNexguardWatermark from "../../ConfigCommon/config-common-section-form-nexguard-watermark";
import ConfigCommonSectionFormAssetsVisible from "../../ConfigCommon/config-common-section-form-assets-visible";
import { useTranslation } from "react-i18next";

interface CoConfigTranscodeByProfileFormProps extends StackProps {
  item: DbOrderBasketItemInfos;
  formid: string;
}

interface TranskodeByIdentityProfile {
  doable: boolean;
  videoProfile: TranskodeVideoProfile | null;
  overrideNeedSDHSubtitle: boolean;
  overrideNeedAudioDescriptionVersion: boolean;
  overrideDoNotUseBurnedPartialSubtitles: boolean;
  overrideUseFullSidecarSubtitles: boolean;
  overrideDoNotUseTextless: boolean;
  overrideDoNotUseDubCards: boolean;
  overrideDoNotUseRestorationCard: boolean;
  overrideAudioMixMappingId: number | null;
  needExternalBroadcasterId: boolean;
  externalBroadcasterId: string | null;
  metadata: DbMetadataFieldInfos[]; // TODO
}

const ConfigTranscodeByProfileForm: React.FC<CoConfigTranscodeByProfileFormProps> = (props) => {
  const { t } = useTranslation();
  const { item, formid } = props;
  const [clips, setClips] = useState<DbAssetObjectInfos[]>([]);
  const dispatch = useDispatch();
  const profiles = useAppSelector(GetBasketProfiles);
  const toggleAvailableOnly = useAppSelector(BasketShowOnlyAvailable);

  const [xmlProfiles, setXmlProfiles] = useState<XmlDeliveryIdentityProfile[]>([]);
  const [xmlProfile, setXmlProfile] = useState<XmlDeliveryIdentityProfile | null>(null);
  const [xmlTechAudioMapping, setXmlTechAudioMapping] = useState<XmlTechAudioMapping | null>(null);
  const [selectedBroadcasterId, setselectedBroadcasterId] = useState<number | undefined>(undefined);
  const titleOriginalLang = useMemo(
    () => GetMetadataByTagName(item.titleInfos, "titleOriginalLanguage")?.value?.value,
    [item, formid]
  );

  const isNeedToSetBroadcater: boolean =
    item.selectedProduct!.settings.needExternalBroadcasterId &&
    item.selectedProduct?.settings.externalBroadcasterId === GlobalConst.BROADCASTER_ID_NEEDED;

  const defaultValuesApplied = useRef<boolean>(false);

  let ratios = ["1.33", "1.66", "1.70", "1.77", "1.85", "2.00"];

  const enqueueCustomSnackbar = useCustomSnackbar();

  // Tgop correctly typed here
  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const tgop: TranskodeGenericOptionsProfile = item.selectedProduct?.settings?.tgop;
  const product: TranskodeByIdentityProfile = item.selectedProduct?.settings;

  // Update tgop or asinput product (depending of item type) object into redux
  const handleUpdateTranscodeParam = (field: TgopField, value: any) => {
    dispatch(UpdateItemSettingsTgopOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update setting root base object into redux
  const handleUpdateSettingsRoot = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const handleBurnedSubtitleSelectionChange = (mapping: XmlTechAudioMapping) => {
    if (!xmlProfile?.subtitleInfos?.burnedSubtitles) return;

    let mixLanguages = mapping!
      .conditions!.map((cond) => cond.language)
      .filter((x) => x !== null && x.value !== null)
      .map((lang) => lang?.value)
      .filter(Distinct);

    let subType: string | null = null;

    for (let sub of xmlProfile?.subtitleInfos?.burnedSubtitles!) {
      if (sub.conditionByDoNotIfVM && mixLanguages.length > 1) continue;

      if (sub.conditionByAudioLanguageIs && !mixLanguages.includes(sub.conditionByAudioLanguageIs.value)) continue;

      if (sub.conditionByAudioLanguageIsNot && mixLanguages.includes(sub.conditionByAudioLanguageIsNot.value)) continue;

      subType = sub.type;
      break;
    }

    if (subType) {
      handleUpdateTranscodeParam(
        TgopField.SubtitleBurned,
        memoSubtitleBurnedValues.find((x) => x.type === subType)
      );
    } else handleUpdateTranscodeParam(TgopField.SubtitleBurned, null);
  };

  // End Methods section

  // Use memo section
  const memoPictureValues: PictureOptionCouple[] = useMemo(() => {
    if (!xmlProfile) return [];

    let array = tgop.picture?.values!;
    let ratiosOk = ratios;

    if (
      xmlProfile!.videoInfos!.contentOriginalAspectRatio &&
      xmlProfile!.videoInfos!.contentOriginalAspectRatio !== "OAR"
    )
      ratiosOk.push(xmlProfile!.videoInfos!.contentOriginalAspectRatio);

    //let titleOriginalLang = GetMetadataByTagName(item.titleInfos, "titleOriginalLanguage")?.value?.value;
    let titleOar = GetMetadataByTagName(item.titleInfos, "titleOriginalPictureFormat")?.value;

    if (titleOar) ratiosOk.push(titleOar);

    let langs: string[] = [];

    // TBD: Chekc if profile language should really filter this? For now, do not filter!
    //if (xmlProfile!.providerInfos!.language) langs.push(xmlProfile!.providerInfos!.language.value!);

    let contentSubTypes: string[] = ["Program", "Program Restored"];

    let avalaibleFormats = tgop.fileFormat?.values?.filter(
      (x) =>
        x.isAvailable &&
        x.codec === xmlProfile.videoInfos?.codec &&
        x.wrapper === xmlProfile.generalInfos?.wrapper &&
        x.videoProfile === xmlProfile.videoInfos.videoProfile &&
        contentSubTypes.includes(x.contentSubType ?? "") &&
        x.chromaSampling === xmlProfile.videoInfos.chromaSampling &&
        x.isHdr === (xmlProfile.videoInfos.hdrType !== "None") &&
        (!xmlProfile.videoInfos.colorSpace || x.colorSpace === xmlProfile.videoInfos.colorSpace)
    );

    let matchOnlyExisting: number[] = avalaibleFormats!.map((x) => x.concernedAssetIds ?? []).flat();

    let res = array
      .filter(
        (x) =>
          (langs.length === 0 || langs.includes(x.language?.value!)) &&
          (!matchOnlyExisting || matchOnlyExisting.some((y) => x.concernedAssetIds?.includes(y))) &&
          /*ratiosOk.includes(x.format?.format!) &&*/
          contentSubTypes.includes(x.contentSubType!)
      )
      .sort((a, b) => {
        if (a.isAvailable > b.isAvailable) {
          return -1;
        } else {
          return 1;
        }
      });

    return res.filter((x) => x.isAvailable);
  }, [xmlProfile, tgop]);

  const memoAudioValues: XmlTechAudioMapping[] = useMemo(() => {
    if (!xmlProfile) return [];

    return MakeAudioValues(xmlProfile, item);
  }, [xmlProfile]);

  const memoSubtitleBurnedValues: SubtitleOption[] = useMemo(() => {
    if (!xmlProfile) return [];

    return MakeSubtitleBurnedValues(xmlProfile, tgop);
  }, [xmlProfile, tgop]);

  const memoSubtitleSidecarValues: SubtitleOption[] = useMemo(() => {
    if (!xmlProfile) return [];

    return MakeSubtitleSidecarValues(xmlProfile, tgop);
  }, [xmlProfile, tgop]);

  const memoSubtitleSidecarSdhValues: SubtitleOption[] = useMemo(() => {
    if (!xmlProfile) return [];

    return MakeSubtitleSdhSidecarValues(xmlProfile, tgop);
  }, [xmlProfile, tgop]);

  const deliveryAttachments = useMemo(() => {
    return xmlProfile?.deliveryInfos?.find((x) => x.assetType === ObjectType.Document);
  }, [xmlProfile]);
  // End Use memo section

  // Form validator
  const formSectionGlobal = useAppSelector(GetSectionIsValid(formid, "sectionGlobal"));
  const formSectionAudio = useAppSelector(GetSectionIsValid(formid, "sectionAudio"));
  const formSectionPicture = useAppSelector(GetSectionIsValid(formid, "sectionPicture"));
  const formSectionSubtitle = useAppSelector(GetSectionIsValid(formid, "sectionSubtitle"));

  // First load
  useEffect(() => {
    const handleResponse = (_profiles: XmlDeliveryIdentityProfilesInfo[] | null) => {
      console.log("product::byprofile:: broadcasterId = " + item.selectedBroadcaster?.id);

      if (!_profiles || _profiles.length === 0) return;

      let profiles: XmlDeliveryIdentityProfile[] | undefined | null = _profiles.find(
        (x) => x.broadcasterId === item.selectedBroadcaster?.id
      )?.profiles;

      if (!profiles) {
        enqueueCustomSnackbar(t("alert:error.noProfileFound"), "error");
        return;
      }

      const profilesWithAvailability = profiles.map((profile) => {
        if (
          item.selectedProduct?.settings.tgop.fileFormat.values.filter(
            (x) => x.isAvailable && x.videoProfile === profile.videoInfos?.videoProfile
          ).length === 0
        ) {
          return { ...profile, isAvailable: false };
        }
        return { ...profile, isAvailable: true };
      });

      setXmlProfiles(profilesWithAvailability);

      // Set default profile or selected one depending of video profile
      let xmlProfileValue: XmlDeliveryIdentityProfile | undefined = undefined;

      if (!item.optionsDone && item.status === BasketItemStatus.DirectivesDone) {
        let index = 0;
        for (let i = 0; i < profilesWithAvailability.length; i++) {
          const x = profilesWithAvailability[i];

          if (x.videoInfos?.videoProfile! >= profilesWithAvailability[index].videoInfos?.videoProfile!) {
            if (
              item.selectedProduct?.settings.tgop.fileFormat.values.some(
                (y) => y.videoProfile === x.videoInfos?.videoProfile && y.concernedAssetIds.some(True)
              )
            ) {
              index = i;
            }
          }
        }
      } else
        xmlProfileValue = profilesWithAvailability.find((x) => x.videoInfos?.videoProfile === product.videoProfile);

      if (xmlProfileValue) setXmlProfile(xmlProfileValue);
      // Set preselected audio mapping if set
      if (product.overrideAudioMixMappingId !== null && xmlProfileValue !== undefined) {
        let audioValues = MakeAudioValues(xmlProfileValue, item);

        setXmlTechAudioMapping(audioValues.find((x) => x.iteration === product.overrideAudioMixMappingId!)!);
        console.log("product::byprofile:: mixMapping override = " + product.overrideAudioMixMappingId);
      }
    };

    if (selectedBroadcasterId === item.selectedBroadcaster?.id) handleResponse(profiles);

    setselectedBroadcasterId(item.selectedBroadcaster?.id);
  }, [item.selectedBroadcaster?.id, profiles]);

  // Apply side effects while Xml profile is changed
  useEffect(() => {
    if (xmlProfile === null) return;

    // Change also the tgop file format to match with missing assets depending of selected xml profile
    let select = xmlProfile as XmlDeliveryIdentityProfile;
    let avalaibleFormats = tgop.fileFormat?.values?.filter(
      (x) =>
        x.isAvailable &&
        x.codec === select.videoInfos?.codec &&
        x.wrapper === select.generalInfos?.wrapper &&
        x.videoProfile === select.videoInfos.videoProfile &&
        (!tgop.picture?.selectedValue?.contentId || x.contentId === tgop.picture?.selectedValue?.contentId) &&
        (!tgop.picture?.selectedValue?.contentSubType ||
          x.contentSubType === tgop.picture?.selectedValue?.contentSubType) &&
        x.chromaSampling === select.videoInfos.chromaSampling &&
        x.isHdr === (select.videoInfos.hdrType !== "None") &&
        x.colorSpace === select.videoInfos.colorSpace
    );
    let avalaibleFormat =
      avalaibleFormats?.find((x) =>
        tgop.picture?.values?.some((y) => y.concernedAssetIds?.some((z) => x.concernedAssetIds?.includes(z)))
      ) ?? avalaibleFormats?.find(True);

    let format =
      avalaibleFormat ??
      tgop.fileFormat?.values?.find(
        (x) =>
          x.codec === select.videoInfos?.codec &&
          x.wrapper === select.generalInfos?.wrapper &&
          x.videoProfile === select.videoInfos.videoProfile &&
          (!tgop.picture?.selectedValue?.contentId || x.contentId === tgop.picture?.selectedValue?.contentId) &&
          (!tgop.picture?.selectedValue?.contentSubType ||
            x.contentSubType === tgop.picture?.selectedValue?.contentSubType) &&
          x.chromaSampling === select.videoInfos.chromaSampling &&
          x.isHdr === (select.videoInfos.hdrType !== "None") &&
          x.colorSpace === select.videoInfos.colorSpace
      );

    handleUpdateTranscodeParam(TgopField.FileFormat, format);
    handleUpdateSettingsRoot(SettingRootField.VideoProfile, xmlProfile.videoInfos?.videoProfile);

    let segment = xmlProfile.timeline?.segments?.find((x) => x.type == TranskodeTimelineSegmentType.DubCard);
    if (
      segment &&
      segment.conditionByAudioLanguageIs &&
      segment.conditionByAudioLanguageIs.value != titleOriginalLang &&
      xmlTechAudioMapping?.languages?.some((x) => x === segment?.conditionByAudioLanguageIs!.value)
    ) {
      let search = tgop.dubCards?.values
        ?.filter((x) => x.language?.value == segment?.conditionByAudioLanguageIs?.value && x.isAvailable)
        .find(True);
      if (search) handleUpdateTranscodeParam(TgopField.DubbingCard, [search]);
      else
        handleUpdateTranscodeParam(TgopField.DubbingCard, [
          new PictureOptionCouple({
            format: new PictureFormat(),
            language: segment.conditionByAudioLanguageIs,
            contentSubType: "Dubbing Card",
          }),
        ]);
    } else handleUpdateTranscodeParam(TgopField.DubbingCard, null);

    console.log("product::byprofile:: profile changed");
  }, [xmlProfile, tgop.picture?.selectedValue]);

  // Default values section
  const selectDefaultAudioVersion = () => {
    // find first available not AD value
    let audioValue = memoAudioValues.find((x) => !x.hasAudioDescription && x.isAvailable);
    // if not take first available AD value
    if (!audioValue) audioValue = memoAudioValues.find((x) => x.isAvailable);
    // if not take N/A
    if (!audioValue)
      audioValue = memoAudioValues.find(
        (x) => (!x.hasAudioDescription && x.isAvailable) || toggleAvailableOnly === x.isAvailable
      );
    // if not take first available AD value
    if (!audioValue) audioValue = memoAudioValues.find((x) => x.isAvailable || toggleAvailableOnly === x.isAvailable);
    // if value => set it
    if (audioValue) {
      handleUpdateSettingsRoot(SettingRootField.OverrideAudioMixMappingId, audioValue.iteration);
      handleUpdateSettingsRoot(SettingRootField.AudioMixMappingId, audioValue.iteration);
      setXmlTechAudioMapping(audioValue);
      setSelectedAudioMix(audioValue);
    }
  };

  // Set AudioMix selected value (for missingAsset)
  const setSelectedAudioMix = (xmlTechAudioMapping: XmlTechAudioMapping | null) => {
    if (!xmlTechAudioMapping) return;

    // Init Selected values
    const audioMixSelectedValues: AudioMixLanguageCouple[] | null = [];

    // Select values by conditions
    xmlTechAudioMapping.conditions?.forEach((condition) => {
      const audioMixValues: AudioMixLanguageCouple[] | null | undefined = tgop.audioMixes?.values?.filter(
        (x) =>
          !x.isCommentary && // [LB] 27-09-2024 Note: Never use commentary => we don't use it in profiles atm, might be a problem so we set to false
          x.mixEnum === condition.mixEnum && // Same mix
          x.isMusicAndEffects === condition.isMusicAndEffects && // Same M&E
          x.isAudioDescription === condition.isAudioDescription && // Same AD
          ((!condition.language && !x.language) || // No language condition => M&E
            x.language?.value === (condition.language?.value == "VO" ? titleOriginalLang : condition.language?.value)) // Same language (VO => title original language)
      );

      // Get first available value or first value
      const audioMixSelectedValue: AudioMixLanguageCouple | null =
        audioMixValues && audioMixValues.length > 0
          ? audioMixValues?.find((x) => x.isAvailable) ?? audioMixValues[0]
          : null;

      if (audioMixSelectedValue !== null) audioMixSelectedValues?.push(audioMixSelectedValue);
    });

    if (audioMixSelectedValues.length > 0) handleUpdateTranscodeParam(TgopField.AudioMixes, audioMixSelectedValues);
    else handleUpdateTranscodeParam(TgopField.AudioMixes, null);
  };

  const selectDefaultPictureVersion = () => {
    // get selected profile
    if (xmlProfile === null) return;

    // Default Content Sub Type
    var defaultContentSubType = "Program";

    // allowed ratios
    var ratios = ["1.33", "1.77"];
    var profileRatio = GetMetadataByTagName(item.titleInfos, "titleOriginalPictureFormat")?.value;

    if (xmlProfile.videoInfos?.contentOriginalAspectRatio && xmlProfile.videoInfos.contentOriginalAspectRatio !== "OAR")
      profileRatio = xmlProfile.videoInfos.contentOriginalAspectRatio;

    if (profileRatio && ratios.indexOf(profileRatio) === -1) ratios.splice(0, 0, profileRatio);

    const pictureVersions = memoPictureValues;
    let pictureSelected = false;

    // Goes through all selectable picture versions
    pictureVersions.forEach((pictureValue) => {
      if (
        !pictureSelected && // set only the first
        pictureValue.format?.format === profileRatio &&
        defaultContentSubType === pictureValue.contentSubType &&
        item.selectedProduct?.settings.tgop.picture !== null &&
        (pictureValue.isAvailable || toggleAvailableOnly === pictureValue.isAvailable)
      ) {
        pictureSelected = true;
        handleUpdateTranscodeParam(TgopField.Picture, pictureValue);
        return;
      }
    });
  };

  const selectDefaultBurnedSubtitleVersion = () => {
    if (xmlProfile !== null && item.selectedProduct?.settings.videoProfile === xmlProfile.videoInfos?.videoProfile) {
      // get selected audio mix
      let selectedIndex = item.selectedProduct?.settings.overrideAudioMixMappingId;
      let selectedAudioMix: XmlTechAudioMapping = new XmlTechAudioMapping();

      memoAudioValues.forEach((audioMix) => {
        if (audioMix.iteration === selectedIndex) selectedAudioMix = audioMix;
      });

      if (
        selectedAudioMix !== null &&
        xmlProfile.subtitleInfos?.burnedSubtitles &&
        xmlProfile.subtitleInfos?.burnedSubtitles?.length > 0
      ) {
        let selectedSubtitleType: string | null = null;

        // search for subtitle matching profile conditions
        let burnedSubtitles = xmlProfile.subtitleInfos?.burnedSubtitles;
        for (let x = 0; x < burnedSubtitles.length; x++) {
          if (
            burnedSubtitles[x].conditionByDoNotIfVM &&
            selectedAudioMix?.languages !== null &&
            selectedAudioMix?.languages !== undefined &&
            selectedAudioMix?.languages?.length > 1
          ) {
            continue;
          }
          if (
            burnedSubtitles[x].conditionByAudioLanguageIs &&
            burnedSubtitles[x].conditionByAudioLanguageIs?.value &&
            selectedAudioMix?.languages !== null &&
            selectedAudioMix?.languages.indexOf(burnedSubtitles[x].conditionByAudioLanguageIs?.value as string) === -1
          ) {
            continue;
          }
          if (
            burnedSubtitles[x].conditionByAudioLanguageIsNot &&
            burnedSubtitles[x].conditionByAudioLanguageIsNot?.value &&
            selectedAudioMix?.languages !== null &&
            selectedAudioMix?.languages.indexOf(burnedSubtitles[x].conditionByAudioLanguageIsNot?.value as string) !==
              -1
          ) {
            continue;
          }

          if (burnedSubtitles[x]) {
            selectedSubtitleType = burnedSubtitles[x].type;
            continue;
          }
        }
        // select subtitle from proposed list
        if (selectedSubtitleType !== null) {
          memoSubtitleBurnedValues.forEach((burnedSubtitle) => {
            if (
              burnedSubtitle.toBeApplied &&
              burnedSubtitle.type === selectedSubtitleType &&
              (toggleAvailableOnly === burnedSubtitle.isAvailable || burnedSubtitle.isAvailable)
            ) {
              handleUpdateTranscodeParam(TgopField.SubtitleBurned, burnedSubtitle);
              return;
            }
          });
        }
      }
    }
  };

  const selectDefaultSidecarSubtitleVersion = useCallback(() => {
    if (!xmlProfile) return;

    // get selected audio mix
    let selectedIndex = item.selectedProduct?.settings.overrideAudioMixMappingId;
    let selectedAudioMix: XmlTechAudioMapping | null = null;
    let selectedSubtitleType: string | null = null;

    memoAudioValues.forEach((audioMix) => {
      if (audioMix.iteration === selectedIndex) selectedAudioMix = audioMix;
    });

    if (
      selectedAudioMix !== null &&
      xmlProfile.subtitleInfos?.sidecarSubtitles &&
      xmlProfile.subtitleInfos?.sidecarSubtitles?.length > 0
    ) {
      // search for subtitle matching profile conditions
      xmlProfile.subtitleInfos?.sidecarSubtitles.forEach((sidecarSubtitle) => {
        // testing conditions...
        if (selectedSubtitleType !== null) return;

        if (sidecarSubtitle.isSDH) {
          return;
        }
        if (
          sidecarSubtitle.conditionByAudioLanguagesAreMoreThan !== null &&
          sidecarSubtitle.conditionByAudioLanguagesAreMoreThan.value
        ) {
          if (
            selectedAudioMix?.languages?.indexOf(sidecarSubtitle.conditionByAudioLanguagesAreMoreThan.value) === -1 ||
            (selectedAudioMix?.languages !== null &&
              selectedAudioMix?.languages.length !== undefined &&
              selectedAudioMix?.languages?.length < 2)
          ) {
            return;
          }
        }

        if (
          item.selectedProduct?.settings.tgop.subtitleBurned.selectedValue !== null &&
          item.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.type === "Partial" &&
          sidecarSubtitle.type === "Full"
        )
          return;

        if (sidecarSubtitle.conditionByPartialBurn) {
          if (
            item.selectedProduct?.settings.tgop.subtitleBurned.selectedValue === null ||
            item.selectedProduct?.settings.tgop.subtitleBurned.selectedValue.type !== "Partial"
          ) {
            return;
          }
        }
        if (
          sidecarSubtitle.conditionByAudioLanguageIs &&
          sidecarSubtitle.conditionByAudioLanguageIs.value &&
          selectedAudioMix?.languages?.indexOf(sidecarSubtitle.conditionByAudioLanguageIs.value) === -1
        ) {
          return;
        }
        if (
          sidecarSubtitle.conditionByAudioLanguageIsNot &&
          sidecarSubtitle.conditionByAudioLanguageIsNot.value &&
          selectedAudioMix?.languages?.indexOf(sidecarSubtitle.conditionByAudioLanguageIsNot.value) !== -1
        ) {
          return;
        }
        // no condition mismatch => sidecar is selected
        if (sidecarSubtitle) {
          selectedSubtitleType = sidecarSubtitle.type;
          return;
        }
      });
    }

    // select subtitle from proposed list
    if (selectedSubtitleType !== null) {
      memoSubtitleSidecarValues?.forEach((sidecarSubtitle) => {
        if (sidecarSubtitle.type === selectedSubtitleType /*&& sidecarSubtitle.isAvailable*/) {
          handleUpdateTranscodeParam(TgopField.SubtitleSidecarFiles, { value: sidecarSubtitle, sdh: false });
          console.debug("sub sidecar modified");
          return;
        }
      });
    }
  }, [xmlProfile, item, memoSubtitleSidecarValues, memoAudioValues, toggleAvailableOnly]);

  const selectDefaultSdhSidecarSubtitleVersion = useCallback(() => {
    if (!xmlProfile) return;

    // get selected audio mix
    let selectedIndex = item.selectedProduct?.settings.overrideAudioMixMappingId;
    let selectedAudioMix: XmlTechAudioMapping | null = null;
    let selectedSubtitleTypeForSdh: string | null = null;

    memoAudioValues.forEach((audioMix) => {
      if (audioMix.iteration === selectedIndex) selectedAudioMix = audioMix;
    });

    if (
      selectedAudioMix !== null &&
      xmlProfile.subtitleInfos?.sidecarSubtitles &&
      xmlProfile.subtitleInfos?.sidecarSubtitles?.length > 0
    ) {
      // search for subtitle matching profile conditions
      let burnedSubtitle = item.selectedProduct?.settings.tgop.subtitleBurned;

      xmlProfile.subtitleInfos?.sidecarSubtitles.forEach((sidecarSubtitle) => {
        if (selectedSubtitleTypeForSdh !== null) return;

        // testing conditions...
        if (!sidecarSubtitle.isSDH) return;

        if (
          sidecarSubtitle.conditionByAudioLanguagesAreMoreThan !== null &&
          sidecarSubtitle.conditionByAudioLanguagesAreMoreThan.value &&
          (selectedAudioMix?.languages?.indexOf(sidecarSubtitle.conditionByAudioLanguagesAreMoreThan.value) === -1 ||
            (selectedAudioMix?.languages !== null &&
              selectedAudioMix?.languages.length !== undefined &&
              selectedAudioMix?.languages.length < 2))
        )
          return;

        if (
          burnedSubtitle.selectedValue !== null &&
          burnedSubtitle.selectedValue.type === "Partial" &&
          sidecarSubtitle.type === "Full"
        )
          return;

        if (
          sidecarSubtitle.conditionByPartialBurn &&
          (burnedSubtitle.selectedValue === null || burnedSubtitle.selectedValue.type !== "Partial")
        )
          return;
        if (
          sidecarSubtitle.conditionByAudioLanguageIs &&
          sidecarSubtitle.conditionByAudioLanguageIs.value &&
          burnedSubtitle.conditionByAudioLanguageIs != undefined &&
          burnedSubtitle.conditionByAudioLanguageIs != null &&
          selectedAudioMix?.languages?.indexOf(burnedSubtitle.conditionByAudioLanguageIs.value) === -1
        )
          return;
        if (
          sidecarSubtitle.conditionByAudioLanguageIsNot &&
          sidecarSubtitle.conditionByAudioLanguageIsNot.value &&
          burnedSubtitle.conditionByAudioLanguageIsNot != undefined &&
          burnedSubtitle.conditionByAudioLanguageIsNot != null &&
          selectedAudioMix?.languages?.indexOf(burnedSubtitle.conditionByAudioLanguageIsNot.value) !== -1
        )
          return;
        // no condition mismatch => sidecar is selected
        if (sidecarSubtitle) {
          selectedSubtitleTypeForSdh = sidecarSubtitle.type;
          return;
        }
      });
    }

    // select subtitle from proposed list
    if (selectedSubtitleTypeForSdh !== null) {
      memoSubtitleSidecarSdhValues?.forEach((sdhSidecarSubtitle) => {
        if (
          sdhSidecarSubtitle.type === selectedSubtitleTypeForSdh /*&&
            (sdhSidecarSubtitle.isAvailable || toggleAvailableOnly === sdhSidecarSubtitle.isAvailable)*/
        ) {
          handleUpdateTranscodeParam(TgopField.SubtitleSidecarFiles, { value: sdhSidecarSubtitle, sdh: true });
          console.debug("sub sidecar sdh modified");
          return;
        }
      });
    }
  }, [xmlProfile, item, memoSubtitleSidecarSdhValues, memoAudioValues, toggleAvailableOnly]);
  // End Default values section

  // Find if selected audio Mixe is in range of displayed value.
  const isAudioMixesInRange = (): boolean => {
    if (!xmlTechAudioMapping) return false;

    return (
      memoAudioValues.find(
        (x) =>
          (!toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)) &&
          x.iteration === xmlTechAudioMapping.iteration &&
          x.label === xmlTechAudioMapping.label
      ) !== undefined
    );
  };

  // Find if selected Subtitle Burned is in range of displayed value.
  const isSubtitleBurnedInRange = (): boolean => {
    if (!tgop || tgop.subtitleBurned === null || !tgop.subtitleBurned.selectedValue === null) return false;

    return (
      memoSubtitleBurnedValues.find(
        (x) =>
          (!toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)) &&
          x.id === tgop.subtitleBurned!.selectedValue!.id
      ) !== undefined
    );
  };

  // Find if selected Subtitle Sidecar is in range of displayed value.
  const isSubtitleSidecarInRange = (): boolean => {
    if (!tgop || tgop.subtitleSidecarFiles === null || !tgop.subtitleSidecarFiles.selectedValues === null) return false;

    return tgop.subtitleSidecarFiles!.selectedValues!.every((y) =>
      memoSubtitleSidecarValues.find(
        (x) => (!toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)) && x.id === y.id
      )
    );
  };

  useDidMountEffect(() => {
    if (
      !defaultValuesApplied.current &&
      xmlProfile &&
      item.status !== BasketItemStatus.OptionsDone &&
      !isNeedToSetBroadcater
    ) {
      // Select Default Audio Version
      if (!xmlTechAudioMapping || !isAudioMixesInRange()) selectDefaultAudioVersion();
      else setSelectedAudioMix(xmlTechAudioMapping);

      // Select Default Picture Version
      if (!item.selectedProduct?.settings.tgop.picture.selectedValue) selectDefaultPictureVersion();
      // Select Default burned subtitle Version
      if (!item.selectedProduct?.settings.tgop.subtitleBurned.selectedValue || !isSubtitleBurnedInRange())
        selectDefaultBurnedSubtitleVersion();
      if (
        !item.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues ||
        !item.selectedProduct?.settings.tgop.subtitleSidecarFiles.selectedValues.length ||
        !isSubtitleSidecarInRange()
      ) {
        // Select Default sidecar subtitle Version
        selectDefaultSidecarSubtitleVersion();
        // Select Default sidecar sdh subtitle Version
        selectDefaultSdhSidecarSubtitleVersion();

        console.debug("default sidear subs hook!");
      }
      defaultValuesApplied.current = true;
    }
  }, [xmlProfile, xmlTechAudioMapping, item]);

  // Load clips
  useEffect(() => {
    if (!item?.episodeNb)
      assetService.GetClipsByObjectId(item?.selectedContent?.id!).then((response) => {
        if (response.results) setClips(response.results);
      });
    else
      assetService.GetClipsByObjectIdWithEpisodeNumber(item?.selectedContent?.id!, item?.episodeNb).then((response) => {
        if (response.results) setClips(response.results);
      });
  }, [item?.selectedContent?.id, item?.episodeNb]);

  const getPictureVersionLabel = (value: any) => {
    let languageText: string = "";
    if (!value.language) languageText = "Textless";
    else {
      if (value.language.value === titleOriginalLang) languageText = "Original " + value.language.label;
      else languageText = "Localized " + value.language.label;
    }

    return (
      languageText +
      (value.format.format ? " - " + value.format.format : "") +
      (value.format.isOriginal ? " (OAR)" : "") +
      " - " +
      value.contentSubType +
      (!value.isAvailable ? " - N/A" : "")
    );
  };

  const getPictureQualityLabel = (value: any) =>
    value.isAvailable === true ? value.videoInfos?.videoProfile : value.videoInfos?.videoProfile + " - Not Available";

  const pictureOptionsStatusText = useMemo(() => {
    const contents: string[] = [];

    if (tgop.picture?.selectedValue) contents.push(getPictureVersionLabel(tgop.picture?.selectedValue));

    if (xmlProfile) contents.push(getPictureQualityLabel(xmlProfile));

    if (product.overrideDoNotUseDubCards) contents.push(t("form:statusText.noDubCard"));
    if (product.overrideDoNotUseTextless) contents.push(t("form:statusText.noTextless"));
    if (product.overrideDoNotUseRestorationCard) contents.push(t("form:statusText.noRestorationCard"));

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    return t("common:configurationRequired");
  }, [tgop, xmlProfile, product]);

  const audioMappingStatusText = useMemo(() => {
    const contents: string[] = [];

    if (xmlTechAudioMapping) contents.push(xmlTechAudioMapping.label || "N/A");
    if (tgop.avoidVarispeed) contents.push(t("form:statusText.avoidVarispeed"));

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    return t("common:configurationRequired");
  }, [xmlTechAudioMapping, tgop]);

  const subtitleOptionsStatusText = useMemo(() => {
    const contents: string[] = [];

    if (tgop.subtitleBurned?.selectedValue) contents.push(GetSubtitleLabel(tgop.subtitleBurned?.selectedValue));

    const noSdhSubtitle = tgop.subtitleSidecarFiles?.selectedValues?.find((x) => x.isSDH === false);
    if (noSdhSubtitle) contents.push(GetSubtitleLabel(noSdhSubtitle));
    const sdhSubtitle = tgop.subtitleSidecarFiles?.selectedValues?.find((x) => x.isSDH === true);
    if (sdhSubtitle) contents.push(GetSubtitleLabel(sdhSubtitle));

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    if (!formSectionSubtitle) return t("form:statusText.configIsRequired");
    else return t("form:statusText.configurationIsOptional");
  }, [tgop, formSectionSubtitle]);

  return (
    <form>
      <Stack spacing={1.5} width={"100%"} {...props}>
        <FormSection
          icon={<CellTowerIcon />}
          title={t("form:section.title.broadcasterSettings")}
          status={formSectionGlobal && !isNeedToSetBroadcater ? "complete" : "warning"}
          statusText={
            formSectionGlobal && !isNeedToSetBroadcater
              ? t("form:statusText.broadcasterId", { id: item.selectedProduct?.settings.externalBroadcasterId })
              : t("common:configurationRequired")
          }
        >
          <Alert color="info" icon={<HelpOutlineIcon />} sx={{ mb: 2 }}>
            {xmlProfile?.generalInfos?.serviceDescription
              ? xmlProfile?.generalInfos?.serviceDescription
              : t("alert:info.youWillGenerateSelfBroadcastedFile", { name: item.selectedBroadcaster?.name })}
          </Alert>
          <Grid container spacing={3}>
            <Grid item md={12}>
              <FormInputText
                label={t("field:label.broadcasterId")}
                startAdornment={<TokenIcon />}
                placeholder={t("field:placeholder.enterBroadcasterId")}
                value={item.selectedProduct?.settings.externalBroadcasterId ?? ""}
                onChange={(v) => handleUpdateSettingsRoot(SettingRootField.BroadcasterId, v.target.value)}
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionGlobal",
                  fieldId: "broadcasterId",
                  initialState: false,
                  required: item.selectedProduct!.settings.needExternalBroadcasterId,
                }}
                disabled={!item.selectedProduct!.settings.needExternalBroadcasterId}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection
          icon={<ConfigFormIcon icon="picture" />}
          title={t("common:pictureOptions")}
          status={formSectionPicture || formSectionPicture === undefined ? "complete" : "warning"}
          statusText={pictureOptionsStatusText}
        >
          <Grid container spacing={3}>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("field:label.pictureVersion")}
                options={memoPictureValues!}
                value={tgop.picture?.selectedValue}
                multiple={false}
                onChange={(v) => handleUpdateTranscodeParam(TgopField.Picture, v)}
                getOptionLabel={(option) => getPictureVersionLabel(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      a.language === null ||
                      a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                      a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                  )
                }
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionPicture",
                  fieldId: "picture",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("field:label.pictureQuality")}
                options={xmlProfiles}
                value={xmlProfile}
                multiple={false}
                onChange={(v) => {
                  setXmlProfile(v);
                  defaultValuesApplied.current = false;
                }}
                getOptionLabel={getPictureQualityLabel}
                isOptionEqualToValue={(option, value) =>
                  option.videoInfos?.videoProfile === value.videoInfos?.videoProfile
                }
                filterOptions={(x, s) =>
                  x.filter((a) => a?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionPicture",
                  fieldId: "xmlProfile",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={product.overrideDoNotUseDubCards}
                      onChange={() =>
                        handleUpdateSettingsRoot(
                          SettingRootField.ForceNoDubbingCardUse,
                          !product.overrideDoNotUseDubCards
                        )
                      }
                    />
                  }
                  label={t("field:label.disregardDubbingCard")}
                />
              </Typography>
            </Grid>
            <Grid item md={4}>
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={product.overrideDoNotUseTextless}
                      onChange={() =>
                        handleUpdateSettingsRoot(SettingRootField.ForceNoTextlessUse, !product.overrideDoNotUseTextless)
                      }
                    />
                  }
                  label={t("field:label.disregardTextlessMaterial")}
                />
              </Typography>
            </Grid>
            <Grid item md={4}>
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={product.overrideDoNotUseRestorationCard}
                      onChange={() =>
                        handleUpdateSettingsRoot(
                          SettingRootField.ForceNoRestorationCardUse,
                          !product.overrideDoNotUseRestorationCard
                        )
                      }
                    />
                  }
                  label={t("field:label.disregardRestorationCard")}
                />
              </Typography>
            </Grid>
          </Grid>
        </FormSection>

        <FormSection
          icon={<ConfigFormIcon icon="audio" />}
          title={t("common:audioMapping")}
          status={formSectionAudio ? "complete" : "warning"}
          statusText={audioMappingStatusText}
        >
          <FormAutocomplete
            label={t("field:label.audioMixes")}
            options={memoAudioValues!.filter((x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable))!}
            value={xmlTechAudioMapping}
            multiple={false}
            onChange={(v) => {
              setXmlTechAudioMapping(v);
              handleUpdateSettingsRoot(SettingRootField.OverrideAudioMixMappingId, v.iteration);
              handleUpdateSettingsRoot(SettingRootField.AudioMixMappingId, v.iteration);
              handleBurnedSubtitleSelectionChange(v);
              setSelectedAudioMix(v);
            }}
            getOptionLabel={(value: any) => value.label}
            isOptionEqualToValue={(option, value) => option.iteration === value.iteration}
            filterOptions={(x, s) =>
              x.filter((a) => a.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
            }
            validationProps={{
              formId: formid,
              formSectionId: "sectionAudio",
              fieldId: "audiomixes",
              required: true,
              initialState: false,
            }}
          />
          <Typography color="text.tier">
            <FormControlLabel
              sx={{ mt: 1.5, ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={tgop.avoidVarispeed}
                  onChange={() => handleUpdateTranscodeParam(TgopField.AudioAvoidVarispeed, null)}
                />
              }
              label={t("field:label.avoidVarispeed")}
            />
          </Typography>
        </FormSection>

        <FormSection
          icon={<ConfigFormIcon icon="subtitles" />}
          title={t("form:section.title.subtitlingOptions")}
          status={
            formSectionSubtitle &&
            ((tgop.subtitleSidecarFiles?.selectedValues && tgop.subtitleSidecarFiles?.selectedValues.length > 0) ||
              tgop.subtitleBurned?.selectedValue)
              ? "complete"
              : !formSectionSubtitle
              ? "warning"
              : "todo"
          }
          statusText={subtitleOptionsStatusText}
        >
          <Grid container spacing={3}>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.burnedSubtitles")}
                options={
                  memoSubtitleBurnedValues!.filter(
                    (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                  )!
                }
                value={tgop.subtitleBurned?.selectedValue}
                multiple={false}
                onChange={(v) => handleUpdateTranscodeParam(TgopField.SubtitleBurned, v)}
                onReset={(v) => handleUpdateTranscodeParam(TgopField.SubtitleBurned, null)}
                getOptionLabel={(value: any) => {
                  return GetSubtitleLabel(value);
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                      a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                  )
                }
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionSubtitle",
                  fieldId: "subBurned",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.sidecarSubtitleFile")}
                options={
                  memoSubtitleSidecarValues!.filter(
                    (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                  )!
                }
                value={tgop.subtitleSidecarFiles?.selectedValues?.find((x) => x.isSDH === false) || null}
                multiple={false}
                onChange={(v) => handleUpdateTranscodeParam(TgopField.SubtitleSidecarFiles, { value: v, sdh: false })}
                getOptionLabel={(value: any) => {
                  return GetSubtitleLabel(value);
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      a.language?.value?.toString().includesAllWords(s.inputValue) ||
                      a.language?.label?.toString().includesAllWords(s.inputValue)
                  )
                }
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionSubtitle",
                  fieldId: "sidecar",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.sidecarSdhSubtitleFile")}
                options={
                  memoSubtitleSidecarSdhValues!.filter(
                    (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                  )!
                }
                value={tgop.subtitleSidecarFiles?.selectedValues?.find((x) => x.isSDH === true) || null}
                multiple={false}
                onChange={(v) => handleUpdateTranscodeParam(TgopField.SubtitleSidecarFiles, { value: v, sdh: true })}
                getOptionLabel={(value: any) => {
                  return GetSubtitleLabel(value);
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      a.language?.value?.toString().includesAllWords(s.inputValue) ||
                      a.language?.label?.toString().includesAllWords(s.inputValue)
                  )
                }
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionSubtitle",
                  fieldId: "sidecarSdh",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>

        <ConfigCommonSectionFormClips item={item} clips={clips} />

        <ConfigCommonSectionFormNexguardWatermark item={item} />

        <ConfigCommonSectionFormStitching item={item} />

        {UserHasAcl(AclTypes.aclOrderIngestGenerated) && <ConfigCommonSectionFormAssetsVisible item={item} />}

        {deliveryAttachments && <ConfigCommonSectionFormAttachments item={item} />}
      </Stack>
    </form>
  );
};

export default ConfigTranscodeByProfileForm;
